<template>
  <div class="account-newsletter-box relative flex flex-col gap-4">
    <Heading v-if="content?.registrationHeadline" size="small" tag="h2">
      {{ content?.registrationHeadline }}
    </Heading>
    <Paragraph v-if="content?.newsletterSubline">
      {{ content?.newsletterSubline }}
    </Paragraph>

    <form class="mt-2" @submit.prevent="onSubmit()">
      <TransitionGroup name="content">
        <div v-if="(!isAlreadyRegistered && !isPending) || isEditMode()" class="editmode-info-box">
          <p v-if="isEditMode()" class="editmode-text">[EDITOR VIEW] Registration Content:</p>
          <Checkbox
            v-model="newsletterFormState.consent"
            :error="!!newsletterVuelidate?.consent?.$errors[0]?.$message"
            :error-text="unref(newsletterVuelidate?.consent?.$errors[0]?.$message)"
            name="consent"
            @change="newsletterVuelidate?.consent?.$touch()"
          >
            <CommonLabel path="/BWF/components/newsletter/newsletterConsent" />
          </Checkbox>
          <button class="btn btn-blue btn-size-sm xl:btn-size-normal mt-6 md:place-self-start">
            <CommonLabel path="BWF/shop/account/overview/newsletterRegistrationButtonLabel" />
          </button>
        </div>

        <div v-if="isEditMode() || isPending" class="editmode-info-box relative items-center">
          <p v-if="isEditMode()" class="editmode-text">[EDITOR VIEW] confirmation Content:</p>
          <div class="notification is-blue gap-x-4">
            <SvgIcon class="notification-icon flex-shrink-0 text-black" name="Checkmark-Circle" />
            <div>
              <p class="flex-shrink-0 hyphens-auto text-base">
                {{ content?.confirmationText ?? "edit confirmation text..." }}
                <button class="font-medium underline">
                  <span>{{ content?.resendEmail ?? "Erneut senden *" }}</span>
                </button>
              </p>
            </div>
          </div>
        </div>
        <div v-if="isEditMode() || isAlreadyRegistered" class="editmode-info-box flex flex-col">
          <p v-if="isEditMode()" class="editmode-text">[EDITOR VIEW] Already registered content:</p>
          <span>{{ content?.informationHeadline }}</span>

          <NuxtLink
            v-if="marketSetupLinks?.newsletterUnsubscribePage"
            :to="unsubscribeLink"
            class="btn btn-blue btn-size-sm xl:btn-size-normal mt-6 md:place-self-start"
          >
            <CommonLabel path="BWF/shop/account/overview/newsletterUnsubscribeButtonLabel" />
          </NuxtLink>
        </div>
      </TransitionGroup>
    </form>
    <Transition class="absolute" mode="out-in" name="loading">
      <LoadingElement v-if="isLoading" class="bg-white/70" :overlay="true" />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { unref } from "vue";
import { Checkbox } from "~/components/clarity-ui/Checkbox";
import { Heading } from "~/components/clarity-ui/Heading";
import { Paragraph } from "~/components/clarity-ui/Paragraph";
import useBritaMiddleware from "~/composables/useBritaMiddleware";
import useMarketSetup from "~/composables/useMarketSetup";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import LoadingElement from "~/templates/elements/LoadingElement.vue";
import SvgIcon from "~/templates/elements/SvgIcon.vue";
import { isEditMode } from "~/utils/content/magnolia";

const { marketSetupLinks } = useMarketSetup();

const { user, isLoggedIn } = useUser();
const { register, getDoiState } = useBritaMiddleware();
const { getLabelByPath } = useCommonLabels();
const { pushError } = useNotifications();
const technicalErrorMessage = getLabelByPath("/BWF/newsletter/doi/technicalErrorMessage");
const isAlreadyRegistered = ref(false);
const isPending = ref(false);
const isLoading = ref(false);

const props = defineProps(["content"]);

onMounted(async () => {
  if (isLoggedIn && !isEditMode()) {
    isLoading.value = true;

    try {
      const data: never = await getDoiState(user?.value?.email);

      if (data?.mailDoiState === "PENDING") {
        isPending.value = true;
      }
      if (data?.donotbulkemail === false) {
        // user has already opted in
        isAlreadyRegistered.value = true;
      }
    } catch (e) {
      console.error(e);
    }

    isLoading.value = false;
  }
});

const { consentValidators } = await useValidation();

const newsletterFormState = reactive({
  consent: null,
});

const rules = computed(() => ({
  consent: {
    ...consentValidators,
  },
}));

const unsubscribeLink = computed(() => {
  const token = btoa(user?.value?.email ?? "");
  return `${marketSetupLinks?.value?.newsletterUnsubscribePage}?token=${token}&source=mybrita`;
});

const newsletterVuelidate = useVuelidate(rules, newsletterFormState);

const onSubmit = async () => {
  isLoading.value = true;
  newsletterVuelidate.value.$touch();
  const valid = await newsletterVuelidate.value.$validate();
  if (valid || isPending.value) {
    try {
      await register(user?.value?.email, props?.content?.interactionId, props?.content?.actionId);
      // email is sent user is registered but pending
      isPending.value = true;
    } catch (e) {
      pushError(technicalErrorMessage);
    }
  }

  isLoading.value = false;
};
</script>

<style>
.account-newsletter-box {
  .editmode-text {
    @apply mb-4 border-b-2 text-xl font-bold text-green;
  }

  .content-enter-active,
  .content-leave-active {
    @apply motion-safe:transition-all motion-safe:duration-700;
  }

  .content-enter-from,
  .content-leave-to {
    position: absolute;
    transform: translateX(30px);
    opacity: 0;
  }

  .loading-enter-active,
  .loading-leave-active {
    @apply motion-safe:transition-opacity motion-safe:duration-500;
  }

  .loading-enter-from,
  .loading-leave-to {
    opacity: 0;
  }
}
</style>
